import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Typography, alpha, Chip } from '@mui/material';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon     from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon    from '@mui/icons-material/ChevronRight';
import HistoryIcon         from '@mui/icons-material/History';
import {
  useListOpenIssuesQuery,
  useListMyBugReportsQuery,
  type IssueSeverity,
  type BugReportStatus,
  type PlatformIssue,
  type BugReport,
} from '../../../store/api/platformApi';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';

const SEV_COLOR: Record<IssueSeverity, string> = {
  info: '#60A5FA', low: '#34D399', medium: '#FBBF24', high: '#F59E0B', critical: '#EF4444',
};
const SEV_LABEL: Record<IssueSeverity, string> = {
  info: 'Aviso', low: 'Menor', medium: 'Medio', high: 'Alto', critical: 'Crítico',
};
const REPORT_COLOR: Record<BugReportStatus, string> = {
  new: '#60A5FA', in_review: '#FBBF24',
  waiting_user: '#C084FC', fixed_pending_push: '#F59E0B', resolved: '#34D399', dismissed: '#A1A1AA', auto_closed: '#71717A',
};
const REPORT_LABEL: Record<BugReportStatus, string> = {
  new: 'Nuevo', in_review: 'Revisando',
  waiting_user: 'Esperando respuesta', fixed_pending_push: 'Listo, falta subir', resolved: 'Resuelto', dismissed: 'Descartado', auto_closed: 'Auto-cerrado',
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1)    return 'recién';
  if (m < 60)   return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24)   return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30)   return `hace ${d} d`;
  return new Date(iso).toLocaleDateString();
}

type Slide =
  | { kind: 'myReports'; items: BugReport[] }
  | { kind: 'issue'; item: PlatformIssue };

const SLIDE_HEIGHT = 132;

interface SupportPanelProps {
  /** Abre la sección "Mis tickets" en Ayuda. */
  onOpenTickets?: () => void;
}

export function SupportPanel({ onOpenTickets }: SupportPanelProps) {
  const golden = P.brand.golden;
  const { data: issues    = [] } = useListOpenIssuesQuery();
  const { data: myReports = [] } = useListMyBugReportsQuery();
  const [idx, setIdx] = useState(0);

  // En el widget solo mostramos los reportes ABIERTOS (nuevo / en revisión).
  // Los resueltos o cerrados se ven en la sección "Mis tickets" de Ayuda.
  const openReports = useMemo(
    () => myReports.filter((r) => r.status === 'new' || r.status === 'in_review'),
    [myReports],
  );

  const slides = useMemo<Slide[]>(() => {
    // El carrusel muestra SOLO novedades (reportes abiertos, incidencias conocidas).
    // El CTA de "reportar o sugerir" ya no vive acá: justo después de mandar un reporte
    // aparecía un cartel enorme ofreciendo reportar de nuevo, arriba de la conversación
    // —que es a lo que el agente vino—. Reportar se hace desde el ícono del encabezado
    // y desde la sección de reportes. (07/08/2026)
    const out: Slide[] = [];
    if (openReports.length > 0) out.push({ kind: 'myReports', items: openReports });
    for (const it of issues) out.push({ kind: 'issue', item: it });
    return out;
  }, [issues, openReports]);

  useEffect(() => {
    if (idx >= slides.length) setIdx(0);
  }, [slides.length, idx]);

  const current = slides[idx];
  const showArrows = slides.length > 1;

  // Sin novedades no hay bloque: el panel arranca directo en la conversación.
  if (slides.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);

  return (
    <Box sx={{
      borderBottom: `1px solid ${alpha(golden, 0.12)}`,
      bgcolor: (t) => alpha(t.palette.background.default, 0.5),
    }}>
      {/* Slide area */}
      <Box sx={{ position: 'relative', px: showArrows ? 5.5 : 1.75, py: 1.25 }}>
        {showArrows && (
          <>
            <IconButton
              size="small"
              onClick={prev}
              sx={{
                position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                color: 'text.secondary', zIndex: 1,
                '&:hover': { color: '#fff', bgcolor: alpha(golden, 0.08) },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={next}
              sx={{
                position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                color: 'text.secondary', zIndex: 1,
                '&:hover': { color: '#fff', bgcolor: alpha(golden, 0.08) },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        )}

        {/* Alto fijo solo cuando hay varios slides (para que no salte al deslizar). Con un
            solo slide se ajusta al contenido y no queda estirado/despegado. */}
        <Box sx={{ minHeight: showArrows ? SLIDE_HEIGHT : 'auto', display: 'flex', alignItems: 'center' }}>
          {current?.kind === 'myReports' && <MyReportsSlide items={current.items} onOpen={onOpenTickets} />}
          {current?.kind === 'issue' && <IssueSlide item={current.item} />}
        </Box>
      </Box>

      {/* Dots indicator */}
      {slides.length > 1 && (
        <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ pb: 1 }}>
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIdx(i)}
              sx={{
                width: i === idx ? 14 : 5, height: 5,
                borderRadius: 3,
                bgcolor: i === idx ? golden : alpha(golden, 0.25),
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            />
          ))}
        </Stack>
      )}

    </Box>
  );
}

// ─── Slides ───────────────────────────────────────────────────────────

// Se puede tener varios reportes abiertos a la vez. Cada uno ocupa UNA línea (estado +
// descripción truncada + antigüedad): con 3 tarjetas de 2 líneas el bloque medía ~175px y
// empujaba la conversación abajo de todo. Mostramos hasta 3 y el resto queda en "Ver todos".
const MAX_PREVIEW = 3;

function MyReportsSlide({ items, onOpen }: { items: BugReport[]; onOpen?: () => void }) {
  const golden = P.brand.golden;
  const preview = items.slice(0, MAX_PREVIEW);
  return (
    <Stack
      onClick={onOpen}
      sx={{ width: '100%', ...(onOpen && { cursor: 'pointer' }) }}
      spacing={0.75}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <HistoryIcon sx={{ fontSize: 15, color: golden }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: golden, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {items.length === 1 ? 'Mi reporte abierto' : `Mis reportes abiertos · ${items.length}`}
        </Typography>
        {onOpen && (
          <Typography sx={{ ml: 'auto', fontSize: 10.5, fontWeight: 700, color: golden }}>
            Ver todos →
          </Typography>
        )}
      </Stack>

      <Stack spacing={0.5}>
        {preview.map((r) => {
          const color = REPORT_COLOR[r.status];
          return (
            <Stack
              key={r._id}
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                px: 0.75, py: 0.5, borderRadius: 1,
                bgcolor: alpha(color, 0.06),
                border: `1px solid ${alpha(color, 0.18)}`,
                borderLeft: `2px solid ${color}`,
              }}
            >
              <Chip
                label={REPORT_LABEL[r.status]}
                size="small"
                sx={{ height: 15, fontSize: 9, fontWeight: 700, bgcolor: alpha(color, 0.18), color, flexShrink: 0 }}
              />
              <Typography sx={{
                flex: 1, minWidth: 0,
                fontSize: 11.5, color: 'text.primary', lineHeight: 1.4,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {r.description}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, flexShrink: 0 }}>
                {relativeTime(r.createdAt)}
              </Typography>
            </Stack>
          );
        })}
        {items.length > preview.length && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10.5, textAlign: 'center' }}>
            +{items.length - preview.length} más
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function IssueSlide({ item }: { item: PlatformIssue }) {
  const color = SEV_COLOR[item.severity];
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: 1.5, flexShrink: 0,
        bgcolor: alpha(color, 0.15), color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${alpha(color, 0.3)}`,
      }}>
        <WarningAmberIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={0.25}>
          <Chip
            label={SEV_LABEL[item.severity]}
            size="small"
            sx={{
              height: 15, fontSize: 9, fontWeight: 800,
              bgcolor: alpha(color, 0.18), color,
              border: `1px solid ${alpha(color, 0.35)}`,
            }}
          />
          <Typography variant="caption" sx={{ color, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Incidencia conocida
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        {item.description && (
          <Typography sx={{
            fontSize: 11.5, color: 'text.secondary', mt: 0.25,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {item.description}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
